import type { ApiClient } from '@repere/api-client';
import {
  PUSH_ANDROID_CHANNEL,
  PUSH_ANDROID_CHANNEL_LABEL,
  PUSH_ANDROID_CHANNEL_URGENCE,
  PUSH_ANDROID_CHANNEL_URGENCE_LABEL,
  type PushPlatform,
} from '@repere/core';
import { color } from '@repere/ui-tokens';
import { PushNotifications } from '@capacitor/push-notifications';
import { estNatif, plateformeNative } from './pont';

/**
 * Notifications hors application — être prévenu téléphone verrouillé, application fermée.
 *
 * Trois pièces doivent être réunies, et l'oubli de n'importe laquelle produit la même
 * panne silencieuse : l'API accepte l'envoi, le système l'accepte aussi, et rien ne
 * s'affiche.
 *
 * 1. **L'autorisation de l'utilisateur.** Elle est demandée, jamais supposée.
 * 2. **Les canaux Android.** Android 8 et suivants n'affichent une notification que si son
 *    canal existe déjà sur l'appareil. Leurs identifiants sont ceux que l'API nomme dans
 *    chaque envoi — ils viennent du noyau partagé pour que les deux ne puissent pas
 *    diverger. Ils sont deux, le quotidien et l'urgence, pour que le destinataire puisse
 *    faire taire l'un sans l'autre.
 * 3. **Le jeton du transport natif.** `register()` rend le jeton FCM sur Android, APNs sur
 *    iOS. C'est délibérément celui-là et non un jeton d'un service intermédiaire : Repère
 *    parle aux deux transports système directement (docs/03 §3). Le changement de coque —
 *    Expo hier, Capacitor aujourd'hui — n'y touche pas : l'API reçoit le même jeton du
 *    même transport, et les appareils déjà enregistrés le restent.
 *
 * Le jeton n'arrive pas en retour d'appel mais par un **événement** : `register()` rend la
 * main tout de suite, et le système répond quand il a joint son service de remise. D'où la
 * promesse posée à la main autour des deux écouteurs, et le délai de garde — sans lui, un
 * téléphone sans réseau laisserait l'attente ouverte pour toujours.
 */

export type EtatNotifications =
  | { etat: 'active'; jeton: string; plateforme: PushPlatform }
  | { etat: 'refusee' }
  | { etat: 'indisponible'; raison: string };

/** Au-delà, on renonce : le système n'a pas joint son service de remise. */
const DELAI_JETON_MS = 15_000;

/**
 * Déclare les deux canaux Android.
 *
 * **Deux, et c'est le point.** L'importance d'un canal appartient au destinataire :
 * Android lui laisse la baisser, canal par canal. Un seul canal partagé signifierait qu'un
 * parent lassé d'être prévenu de chaque dépense fait taire du même geste la seule
 * notification que le produit se permet d'imposer — et ne l'apprendrait que le jour où
 * elle compte. Séparés, on peut rendre le quotidien silencieux **et** rester joignable.
 *
 * Les deux sont créés à chaque démarrage, et non une fois : `createChannel` est
 * idempotent, tandis qu'un canal créé conditionnellement manquerait sur les téléphones où
 * l'application était déjà installée avant qu'il n'existe. Un envoi vers un canal absent
 * est accepté par FCM et **jamais affiché** — la panne la plus difficile à voir.
 *
 * Ce que le code ne peut pas donner : `bypassDnd`. Android le réserve aux applications qui
 * ont obtenu l'accès « Ne pas déranger », lequel se demande à l'utilisateur depuis les
 * réglages du système. L'importance maximale et le canal séparé sont ce qui s'obtient sans
 * cette permission ; c'est déjà ce qui distingue une alerte d'une dépense.
 */
async function preparerCanauxAndroid(): Promise<void> {
  if (plateformeNative() !== 'android') return;

  await PushNotifications.createChannel({
    id: PUSH_ANDROID_CHANNEL,
    name: PUSH_ANDROID_CHANNEL_LABEL,
    importance: 5,
    // Le contenu s'affiche sur l'écran verrouillé : il n'y en a pas, par construction.
    visibility: 1,
    lightColor: color.navy,
  });

  await PushNotifications.createChannel({
    id: PUSH_ANDROID_CHANNEL_URGENCE,
    name: PUSH_ANDROID_CHANNEL_URGENCE_LABEL,
    description:
      'Ce qu’un parent a signalé comme urgent. Deux par dossier et par 24 heures au plus.',
    importance: 5,
    visibility: 1,
    // Vibration et son : ce canal existe pour être remarqué téléphone dans une poche.
    // Le destinataire garde ses deux défenses dans l'application — couper le son des
    // alertes, désactiver l'urgence — et celles-ci l'emportent sur ce que l'auteur demande.
    vibration: true,
    sound: 'default',
    lightColor: color.amber,
  });
}

/** Demande l'autorisation si elle n'a pas déjà été accordée ou refusée. */
async function autoriser(): Promise<boolean> {
  const actuel = await PushNotifications.checkPermissions();
  if (actuel.receive === 'granted') return true;
  // Un refus définitif ne se redemande pas : seul le réglage système peut le lever.
  if (actuel.receive === 'denied') return false;

  const demande = await PushNotifications.requestPermissions();
  return demande.receive === 'granted';
}

type IssueJeton = { jeton: string } | { raison: string };

/**
 * Enregistre l'appareil auprès du système et attend le jeton de remise.
 *
 * Les écouteurs sont posés **avant** `register()` : le système peut répondre dans le même
 * tour de boucle, et deux écouteurs posés après auraient manqué la réponse qu'ils
 * attendent. Ils sont retirés dans tous les cas, y compris au délai de garde — un écouteur
 * laissé derrière se rejouerait au prochain renouvellement de jeton, pour personne.
 */
async function obtenirJeton(): Promise<IssueJeton> {
  let resoudre: (issue: IssueJeton) => void = () => undefined;
  const attente = new Promise<IssueJeton>((tenir) => {
    resoudre = tenir;
  });

  const ecouteurs = await Promise.all([
    PushNotifications.addListener('registration', (jeton) => resoudre({ jeton: jeton.value })),
    PushNotifications.addListener('registrationError', (erreur) =>
      resoudre({ raison: String(erreur.error) }),
    ),
  ]);

  const minuterie = setTimeout(
    () => resoudre({ raison: 'Le système n’a pas rendu de jeton de remise.' }),
    DELAI_JETON_MS,
  );

  try {
    await PushNotifications.register();
    return await attente;
  } catch (cause) {
    // Typiquement : `google-services.json` absent du build Android, ou droit de
    // notification distante non signé côté iOS.
    return { raison: cause instanceof Error ? cause.message : 'Enregistrement refusé.' };
  } finally {
    clearTimeout(minuterie);
    for (const ecouteur of ecouteurs) await ecouteur.remove();
  }
}

/**
 * Réunit ce qui ne dépend que de l'appareil : autorisation, canal, jeton de remise.
 *
 * Séparé de l'enregistrement parce que cela se vérifie sans compte et sans réseau — un
 * écran de diagnostic s'en sert pour dire, avant même toute connexion, si ce téléphone
 * pourra être prévenu.
 */
export async function preparerNotifications(): Promise<EtatNotifications> {
  const plateforme = plateformeNative();
  if (!estNatif() || plateforme === null) {
    // Le navigateur reçoit ses nouveautés à l'écran et par e-mail. Ce n'est pas une panne
    // à signaler, c'est l'autre moitié du produit.
    return { etat: 'indisponible', raison: 'Hors application mobile.' };
  }

  await preparerCanauxAndroid();
  if (!(await autoriser())) return { etat: 'refusee' };

  const issue = await obtenirJeton();
  if ('raison' in issue) return { etat: 'indisponible', raison: issue.raison };

  return { etat: 'active', jeton: issue.jeton, plateforme };
}

/**
 * Active les notifications et rattache l'appareil au compte connecté.
 *
 * À appeler à **chaque** ouverture de session, et non une seule fois : le système
 * renouvelle ce jeton quand bon lui semble — restauration de sauvegarde, réinstallation,
 * effacement des données. Un enregistrement fait une fois pour toutes finirait par ne
 * plus désigner aucun appareil, sans que rien ne le signale.
 */
export async function activerNotifications(client: ApiClient): Promise<EtatNotifications> {
  const etat = await preparerNotifications();
  if (etat.etat !== 'active') return etat;

  await client.request('/notifications/devices', {
    method: 'POST',
    body: { token: etat.jeton, platform: etat.plateforme },
  });
  dernierJeton = etat.jeton;

  return etat;
}

/**
 * Prévient l'application qu'une notification vient d'arriver ou d'être touchée.
 *
 * Deux événements, un seul effet — et c'est voulu :
 *
 * - `pushNotificationReceived` : l'application est **au premier plan**. Android ne pose
 *   alors rien dans le volet, il remet le message au code ; sans écouteur, il n'arrive donc
 *   littéralement rien. C'est le cas du parent qui a l'application ouverte pendant que
 *   l'autre écrit : le fait était déjà en base, la pastille l'ignorait.
 * - `pushNotificationActionPerformed` : la notification a été touchée depuis le volet.
 *   L'application revient au premier plan, et ce qu'on vient de lui demander de montrer
 *   est précisément ce qu'elle n'a pas encore.
 *
 * Ce que l'écouteur fait est volontairement pauvre : il **redemande**, il ne lit pas la
 * charge. Le message ne porte aucun contenu (docs/02 §10), et son champ `data` ne dit que
 * `digest` ou `urgence` — s'en servir pour deviner ce qui a changé reviendrait à faire
 * confiance à un texte venu du réseau pour décider de l'état de l'écran, quand la base,
 * elle, sait.
 */
export async function brancherNotificationsRecues(
  auFait: () => void,
): Promise<() => Promise<void>> {
  if (!estNatif()) return () => Promise.resolve();

  const ecouteurs = await Promise.all([
    PushNotifications.addListener('pushNotificationReceived', () => auFait()),
    PushNotifications.addListener('pushNotificationActionPerformed', () => auFait()),
  ]);

  return async () => {
    for (const ecouteur of ecouteurs) await ecouteur.remove();
  };
}

/**
 * Le dernier jeton enregistré, pour savoir quoi détacher à la déconnexion.
 *
 * En mémoire du module et non dans le stockage : il ne survit pas à la fermeture de
 * l'application, et c'est sans conséquence — le compte suivant sur cet appareil se le
 * réattribuera de toute façon au prochain enregistrement.
 */
let dernierJeton: string | null = null;

/**
 * Fait taire cet appareil. À appeler avant de fermer la session.
 *
 * L'échec est avalé : une déconnexion ne doit jamais être empêchée par un appel réseau.
 * Le jeton restera enregistré jusqu'à ce que le transport le déclare mort, et le compte
 * suivant sur cet appareil se le réattribuera de toute façon.
 */
export async function oublierAppareil(client: ApiClient, jeton: string): Promise<void> {
  try {
    await client.request('/notifications/devices', { method: 'DELETE', body: { token: jeton } });
  } catch {
    /* déconnexion prioritaire */
  }
}

/**
 * Détache cet appareil du compte qui s'en va.
 *
 * À appeler **avant** de fermer la session : après, il n'y a plus de jeton d'accès pour
 * autoriser le détachement, et l'appareil continuerait de recevoir les nouveautés d'un
 * compte auquel il n'appartient plus — sur un téléphone prêté, c'est une fuite.
 *
 * Sans coque, ou sans enregistrement préalable, ne fait rien.
 */
export async function oublierAppareilCourant(client: ApiClient): Promise<void> {
  if (dernierJeton === null) return;
  const jeton = dernierJeton;
  dernierJeton = null;
  await oublierAppareil(client, jeton);
}
